import { defineStore } from 'pinia'
import { BackgroundRemovalStatus } from '~/static/enums/background-removal-status'
import { useApi } from '~/composables/FetchApi'
import { track } from '~/utils/Amplitude'
import { LoadingProcessType } from '~/static/enums/image/loading-process-type'
import { Generations } from '~/static/types/user'
import { BackgroundGenerationStatus } from '~/static/enums/image/background-generation-status'
import { useEditorStore } from '~/store/editor'

interface ImageStore {
  timers: { [uuid: string]: NodeJS.Timeout }
  images: { [uuid: string]: string }
  enhanceResults: { [uuid: string]: { status: BackgroundGenerationStatus, urls: string[] } }
  isBackRemovingError: boolean
  loadingProcessType: LoadingProcessType | undefined
}

interface LoadImageResponse {
  url: string
}
interface BackRemovalResponse {
  task_uuid: string
}
interface GetImageByTaskResponse {
  status: BackgroundRemovalStatus
  url: string | null
}

interface EnhanceImageResponse {
  task_uuid: string
  generations: Generations
}

interface GetEnhanceResultResponse {
  status: BackgroundGenerationStatus
  urls: string[]
}

export const useImageStore = defineStore('image', {
  state: (): ImageStore => {
    return {
      timers: {},
      images: {},
      enhanceResults: {},
      isBackRemovingError: false,
      loadingProcessType: undefined,
    }
  },
  actions: {
    async loadImage(
      image: File,
      loadingProcessType: LoadingProcessType | undefined = undefined,
    ): Promise<string | undefined> {
      // 15728640 is 15Mb
      if (image.size >= 15728640) {
        useNuxtApp().$event('popup:show', {
          text: 'Максимальный размер фото 15 МБ',
        })
        return
      }

      const formData = new FormData()
      formData.append('image', image)

      this.loadingProcessType = loadingProcessType
      const { response, error } = await useApi<LoadImageResponse>('card.loadImage', {}, HttpMethod.POST, formData)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        this.loadingProcessType = undefined
      } else if (response) {
        return response.url
      }
    },
    async removeBack(imageUrl: string): Promise<string> {
      const { response, error } = await useApi<BackRemovalResponse>('card.backgroundRemoval', {}, HttpMethod.POST, {
        url: imageUrl,
      })
      if (error) {
        this.isBackRemovingError = true
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        this.isBackRemovingError = false
        track('Remove background')
        this.getImageByTask(response.task_uuid)
        return response.task_uuid
      }
      return Promise.reject(error ?? 'No response')
    },
    async getImageByTask(uuid: string) {
      const { response, error } = await useApi<GetImageByTaskResponse>('card.getImageWithBackgroundRemoved', {
        task_uuid: uuid,
      })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        if (response.status === BackgroundRemovalStatus.Error) {
          this.isBackRemovingError = true
          console.log(response)
          return
        }
        if (response.status === BackgroundRemovalStatus.Completed && response.url !== null) {
          this.images[uuid] = response.url
          delete this.timers[uuid]
          return
        }
        this.timers[uuid] = setTimeout(() => {
          this.getImageByTask(uuid)
        }, 1000)
      }
    },
    async enhanceImage(imageUrl: string, preserveAlpha: boolean): Promise<{
      taskUuid: string
      generations: Generations
    }> {
      const editorStore = useEditorStore()
      const cardId = editorStore.id

      const body: { image_url: string, preserve_alpha: boolean, card_id?: number } = {
        image_url: imageUrl,
        preserve_alpha: preserveAlpha,
      }
      if (cardId !== null) {
        body.card_id = cardId
      }

      const {
        response,
        error,
      } = await useApi<EnhanceImageResponse>('generations.enhanceImage', {}, HttpMethod.POST, body)
      if (error || !response) {
        // eslint-disable-next-line no-console
        console.log(error?.error_code, error?.error_msg)
        return Promise.reject(error ?? 'No response')
      }

      await this.getEnhanceResult(response.task_uuid)

      return {
        taskUuid: response.task_uuid,
        generations: response.generations,
      }
    },
    async getEnhanceResult(taskUuid: string) {
      const { response, error } = await useApi<GetEnhanceResultResponse>('generations.getResult', {
        task_uuid: taskUuid,
      })
      if (error || !response) {
        // eslint-disable-next-line no-console
        console.log(error?.error_code, error?.error_msg)
        this.enhanceResults[taskUuid] = {
          status: BackgroundGenerationStatus.Error,
          urls: [],
        }
        return
      }
      if (response.status === BackgroundGenerationStatus.Pending) {
        this.timers[taskUuid] = setTimeout(() => {
          this.getEnhanceResult(taskUuid)
        }, 1000)
        return
      }
      this.enhanceResults[taskUuid] = {
        status: response.status,
        urls: response.urls,
      }
      delete this.timers[taskUuid]
    },
  },
})
